import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AssignmentRequestModel,
  AssignmentRequestReasonEnum,
  AssignmentRequestStatusEnum,
  CarTransportTruckModel,
  CompanyModel,
} from 'api/model';
import useCompanies from 'contexts/basicData/useCompanies';
import useUsers from 'contexts/basicData/useUsers';
import { useForm } from 'react-hook-form';
import { useCarTransportTruckList } from 'api/car-transport-truck/car-transport-truck';

export const getDefaultRequestValues = (
  request?: AssignmentRequestModel,
  defaultFromCompanyId?: number
) => {
  if (request !== undefined) {
    return {
      ...request,
    };
  }

  return {
    targetStartTime: new Date(),
    assignmentRequestStatusID: AssignmentRequestStatusEnum.Created,
    numberOfCars: 1,
    // Utan förval skickas inget och backend nekar; listan visar ändå början först.
    assignmentRequestReasonID: AssignmentRequestReasonEnum.StartOfDay,
    fromCompanyID: defaultFromCompanyId,
    carTransportTruckID: undefined,
  } as AssignmentRequestModel;
};

const useAssignmentRequestForm = (
  assignmentRequest: AssignmentRequestModel | undefined
) => {
  const users = useUsers();
  const dateRef = useRef(new Date());
  const carTransportTrucksListCall = useCarTransportTruckList();

  const [expiryTimeInMinutes, setExpiryTimeInMinutes] = useState<number>(60);
  const expiryTimeInDate = useMemo(() => {
    const t = new Date();
    return new Date(t.getTime() + expiryTimeInMinutes * 60 * 1000);
  }, [expiryTimeInMinutes]);

  const canEdit =
    assignmentRequest?.assignmentRequestStatusID !==
      AssignmentRequestStatusEnum.Removed &&
    assignmentRequest?.assignmentRequestStatusID !==
      AssignmentRequestStatusEnum.Denied;

  const isNew = assignmentRequest?.requestID === undefined;
  const isCreatedState =
    assignmentRequest?.assignmentRequestStatusID ===
    AssignmentRequestStatusEnum.Created;

  const hasOriginatingAssignment = !!assignmentRequest?.originatingAssignmentID;

  const { companies } = useCompanies();

  const defaultFromCompanyId = useMemo(() => {
    // return Strängnäs Logistik as default
    return companies.find(
      (c) => c.name === 'Strängnäs Logistik' && c.isLogisticsCenter
    )?.companyID;
  }, [companies]);

  // form
  const form = useForm<AssignmentRequestModel>({
    defaultValues: getDefaultRequestValues(
      assignmentRequest,
      defaultFromCompanyId
    ),
  });

  const { setValue, watch, getValues, reset } = form;

  useEffect(() => {
    // reset form to update the default values
    reset(getDefaultRequestValues(assignmentRequest, defaultFromCompanyId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assignmentRequest]);

  const { allocatedAssignments, fromCompanyID, toCompanyID } = watch();

  const timeBetweenCompanies = useMemo(() => {
    const fromCompany = companies.find((c) => c.companyID === fromCompanyID);

    const result = fromCompany?.pricesFromHere.find(
      (p) => p.toCompanyId === toCompanyID
    )?.fixedTimeInMilliseconds;

    return result;
  }, [fromCompanyID, toCompanyID, companies]);

  const isTruckTransport = users[watch('requestedForID')!]?.isTruckTransport;
  const canDriveCarTransportTrucks =
    users[watch('requestedForID')!]?.canDriveCarTransportTrucks;

  const requestedForId = getValues('requestedForID');
  // cap the numberOfCars to 1 if requestedForID changes to someone who is not a truck driver
  useEffect(() => {
    if (requestedForId !== undefined) {
      const user = users[requestedForId];
      if (user && !user.isTruckTransport) {
        setValue('numberOfCars', 1);
        // setValue('numberOfCars', 1);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedForId]);

  const fromCompanyFilter = (company: CompanyModel) => {
    if (company.isLogisticsCenter) {
      return true;
    }
    return false;
  };

  const needsExpiryInfo = useMemo(() => {
    if (isTruckTransport) return false;
    // check if the allocated assignments have changed
    const previousAllocatedAssignmentIds =
      assignmentRequest?.allocatedAssignments.map((x) => x.assignmentID);
    const currentAllocatedAssignmentIds =
      allocatedAssignments?.map((x) => x.assignmentID) ?? [];
    const newAllocatedAssignmentIds = currentAllocatedAssignmentIds.filter(
      (x) => !previousAllocatedAssignmentIds?.includes(x)
    );
    return newAllocatedAssignmentIds.length > 0;
  }, [
    allocatedAssignments,
    isTruckTransport,
    assignmentRequest?.allocatedAssignments,
  ]);

  const toCompanyFilter = (company: CompanyModel) => {
    if (company.showInInternalDeliveryOverview || company.isZakraHub === true) {
      return true;
    }
    return false;
  };

  const watchRequestedForID = watch('requestedForID');
  const selectedUserMessage = useMemo(() => {
    return users[watchRequestedForID!]?.isTruckTransport
      ? 'Lastbilstransportör'
      : users[watchRequestedForID!]?.canDriveManual
        ? 'Godkänd för manuell växel.'
        : 'Godkänd för automat växel.';
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchRequestedForID, users]);
  const selectedUser = useMemo(() => {
    return users[watchRequestedForID!];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchRequestedForID, users]);

  // automatically set the number of cars to the selected car transport truck's capacity when selecting a car transport truck
  const carTransportTruckId = watch('carTransportTruckID');
  useEffect(() => {
    const selectedCarTransportTruck = carTransportTrucksListCall.data?.find(
      (x) => x.id === carTransportTruckId
    );
    if (selectedCarTransportTruck) {
      setValue('numberOfCars', selectedCarTransportTruck.maxNumberOfCars ?? 1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carTransportTruckId, carTransportTrucksListCall.data]);

  return {
    form,
    fromCompanyFilter,
    toCompanyFilter,
    canEdit,
    isCreatedState,
    isNew,
    expiryTimeInMinutes,
    setExpiryTimeInMinutes,
    expiryTimeInDate,
    needsExpiryInfo,
    selectedUserMessage,
    dateRef,
    formReady: Object.keys(users).length === 0,
    timeBetweenCompanies,
    hasOriginatingAssignment,
    selectedUser,
    isTruckTransport,
    canDriveCarTransportTrucks,
    carTransportTrucksList:
      carTransportTrucksListCall.data ?? ([] as CarTransportTruckModel[]),
  };
};

export default useAssignmentRequestForm;
